import { useIntl } from 'react-intl';

import { useFreeTrialClick } from '@/components/plans/hooks/useFreeTrialClick';
import PlanTile from '@/components/plans/PlanTile';
import GridWrapper from '@/components/ui/GridWrapper';
import { useAnalytics } from '@/hooks/analytics';
import { useFormattedCurrency } from '@/hooks/purchase';
import { usePricesState, useUserState } from '@/hooks/store';
import redirect from '@/utils/legacy/redirect';
import { userCanTrial } from '@/utils/subscriptions';

import messages from './messages';

const PlanPicker = () => {
	const onFreeTrialClick = useFreeTrialClick();
	const { formatMessage } = useIntl();
	const formatCurrency = useFormattedCurrency();
	const prices = usePricesState();
	const user = useUserState();
	const { logEventAsync } = useAnalytics();

	async function onLifetimeClick(): Promise<void> {
		await logEventAsync({
			eventName: 'Free Trial Details : Forever : Clicked',
		});
		redirect('/subscribe?plan=lifetime');
	}

	function getFreeTrialPlanName(): string {
		if (userCanTrial(user) && !prices?.coupons?.yearly) {
			return formatMessage(messages.freeTrialPlanName);
		}

		if (prices?.coupons?.yearly) {
			return formatMessage(messages.couponPlanName, {
				percent: prices?.coupons?.yearly?.percent_off,
			});
		}

		return formatMessage(messages.genericYearlyPlanName);
	}

	function getFreeTrialPlanDetails(): string {
		if (userCanTrial(user) && !prices?.coupons?.yearly) {
			return formatMessage(messages.freeTrialDetails, {
				formattedPrice: formatCurrency(prices?.current?.yearly),
			});
		}

		const nextPrice =
			prices?.coupons?.yearly?.duration === 'forever' ? prices?.current?.yearly : prices?.original?.yearly;

		return formatMessage(messages.subscribeDetails, {
			nextPrice: formatCurrency(nextPrice),
			currentPrice: formatCurrency(prices?.current?.yearly),
		});
	}

	return (
		<GridWrapper>
			<PlanTile
				banner={messages.freeTrialBanner}
				planName={getFreeTrialPlanName()}
				details={getFreeTrialPlanDetails()}
				cta={messages.freeTrialButton}
				theme="limited"
				onClick={onFreeTrialClick}
			/>
			<PlanTile
				banner={messages.foreverBanner}
				planName={formatMessage(messages.foreverPlanName)}
				details={formatMessage(messages.foreverDetails, {
					formattedPrice: formatCurrency(prices?.current?.lifetime),
				})}
				cta={messages.foreverButton}
				onClick={onLifetimeClick}
			/>
		</GridWrapper>
	);
};

export default PlanPicker;
